import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ErrorAlert } from '../../components/ErrorAlert';
import { usePageTitle } from '../../components/shell/AppShell';
import { useMyPreferences, useUpdatePreferences } from '../../api/queries';
import { mergePreferencePatch, parsePreferences } from '../../theme/preferences';
import type { ThemeMode } from '../../theme/preferences';

const PRIMARY_PRESETS: { hex: string; label: string }[] = [
  { hex: '#1f3b6f', label: 'ARMS blue' },
  { hex: '#0c1222', label: 'Slate' },
  { hex: '#1e293b', label: 'Midnight' },
  { hex: '#0f766e', label: 'Forest' },
  { hex: '#7c2d12', label: 'Burnt' },
  { hex: '#4f46e5', label: 'Indigo' },
];

const ACCENT_PRESETS: { hex: string; label: string }[] = [
  { hex: '#7d4f8f', label: 'Plum (default)' },
  { hex: '#1f3b6f', label: 'ARMS blue' },
  { hex: '#0f9b6e', label: 'Forest' },
  { hex: '#c2410c', label: 'Ember' },
  { hex: '#4f46e5', label: 'Indigo' },
  { hex: '#0e7490', label: 'Teal' },
];

export default function AppearancePage() {
  usePageTitle('Appearance');
  const prefsQ = useMyPreferences();
  const update = useUpdatePreferences();
  const [snack, setSnack] = useState(false);

  const initial = parsePreferences(prefsQ.data);
  const [mode, setMode]       = useState<ThemeMode>(initial.themeMode ?? 'system');
  const [primary, setPrimary] = useState<string | undefined>(initial.primaryHex);
  const [accent, setAccent]   = useState<string | undefined>(initial.accentHex);

  // Sync local state with the fetched prefs once they arrive (or change).
  useEffect(() => {
    if (prefsQ.data) {
      const next = parsePreferences(prefsQ.data);
      setMode(next.themeMode ?? 'system');
      setPrimary(next.primaryHex);
      setAccent(next.accentHex);
    }
  }, [prefsQ.data]);

  const dirty =
    mode !== (initial.themeMode ?? 'system') ||
    primary !== initial.primaryHex ||
    accent !== initial.accentHex;

  // Save must never fire `mergePreferencePatch(undefined, patch)` — that
  // merges this page's theme keys onto `{}` instead of the real stored
  // blob, which silently WIPES every other preference key (e.g. the SAS
  // analyser's saved colour palette under `sasAnalyzer`) once the PUT
  // lands. Gate on the query's error/undefined-data state directly on the
  // button rather than relying on the loading/error branches below to keep
  // Save unreachable — that way a future reshuffle of this render tree
  // can't silently reopen the gap.
  const prefsUnavailable = prefsQ.isError || prefsQ.data === undefined;

  const save = () => {
    if (prefsUnavailable) return;
    // PUT /me/preferences REPLACES the whole stored object — merge this
    // page's theme keys onto whatever else is already saved (e.g. the SAS
    // analyser's per-user colour palette under `sasAnalyzer`) rather than
    // clobbering it. See mergePreferencePatch's doc comment.
    update.mutate(
      mergePreferencePatch(prefsQ.data, { themeMode: mode, primaryHex: primary ?? null, accentHex: accent ?? null }),
      { onSuccess: () => setSnack(true) },
    );
  };

  return (
    <>
      <Stack spacing={3} sx={{ maxWidth: 720 }}>
        <Box>
          <Typography variant="h4">Appearance</Typography>
          <Typography variant="body2" color="text.secondary">
            Per-user portal preferences. Synced across browsers via your account.
          </Typography>
        </Box>

        {prefsQ.isLoading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={20} />
            <Typography>Loading preferences…</Typography>
          </Stack>
        ) : (
          <>
            {prefsUnavailable && (
              <ErrorAlert severity="warning">
                Preferences unavailable — retry. Saving is disabled in the meantime so your other
                saved preferences can't be overwritten with a blank value.
                {prefsQ.error?.message ? ` (${prefsQ.error.message})` : null}
              </ErrorAlert>
            )}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">Theme mode</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Light, dark, or follow your operating system.
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={mode}
                  onChange={(_, v) => { if (v) setMode(v as ThemeMode); }}
                  size="small"
                >
                  <ToggleButton value="light">Light</ToggleButton>
                  <ToggleButton value="dark">Dark</ToggleButton>
                  <ToggleButton value="system">System</ToggleButton>
                </ToggleButtonGroup>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">Primary brand colour</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Overrides the operator's primary brand for your account only. Sidebar, primary buttons, and
                  active-tile stripes adopt this colour. Pick "Operator default" to inherit the operator's
                  brand again — useful when ACES support staff bounce between operators.
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1.5 }}>
                  <Swatch
                    label="Operator default"
                    selected={primary === undefined}
                    onClick={() => setPrimary(undefined)}
                  />
                  {PRIMARY_PRESETS.map((p) => (
                    <Swatch
                      key={p.hex}
                      hex={p.hex}
                      label={p.label}
                      selected={primary === p.hex}
                      onClick={() => setPrimary(p.hex)}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">Accent colour</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Overrides the operator's accent for your account only. Applies to chips,
                  highlights, and small UI accents.
                </Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" sx={{ gap: 1.5 }}>
                  <Swatch
                    label="Operator default"
                    selected={accent === undefined}
                    onClick={() => setAccent(undefined)}
                  />
                  {ACCENT_PRESETS.map((p) => (
                    <Swatch
                      key={p.hex}
                      hex={p.hex}
                      label={p.label}
                      selected={accent === p.hex}
                      onClick={() => setAccent(p.hex)}
                    />
                  ))}
                </Stack>
              </CardContent>
            </Card>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {update.error && (
                <ErrorAlert sx={{ flex: 1 }}>
                  {(update.error as { message?: string }).message ?? 'Failed to save.'}
                </ErrorAlert>
              )}
              <Button
                variant="contained"
                disabled={!dirty || update.isPending || prefsUnavailable}
                onClick={save}
              >
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </Stack>
          </>
        )}
      </Stack>

      <Snackbar
        open={snack}
        autoHideDuration={2000}
        onClose={() => setSnack(false)}
        message="Preferences saved."
      />
    </>
  );
}

function Swatch({
  hex, label, selected, onClick,
}: {
  hex?: string;
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Stack
      onClick={onClick}
      sx={{
        cursor: 'pointer', textAlign: 'center', minWidth: 96,
      }}
    >
      <Box
        sx={{
          width: 64, height: 64, borderRadius: 1,
          mx: 'auto', mb: 0.5,
          bgcolor: hex ?? 'transparent',
          border: '2px solid',
          borderColor: selected ? 'primary.main' : 'divider',
          backgroundImage: hex ? undefined : 'repeating-linear-gradient(45deg, #ddd 0 4px, transparent 4px 8px)',
        }}
      />
      <Typography variant="caption">{label}</Typography>
    </Stack>
  );
}
